import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { PublishedContextValue } from '@/components/views/published/PublishedContext';
import { usePublishedContext } from '@/components/views/published/PublishedContext';
import type { Item } from '@/lib/api/types/items';
import { useItemsBulkFetchQuery } from '@/lib/queries/common/useGetItemsBulkFetchQuery';
import { commonQueryKeys } from '@/lib/query-keys/common';

type ItemSummary = Pick<Item, 'id' | 'name' | 'itemTypeId'>;

function resolvePublishedItems(ctx: PublishedContextValue, itemIds: number[]): ItemSummary[] {
  const panelItemId = ctx.panelItemId;
  const panelData = panelItemId !== undefined ? ctx.getItemPanelData(panelItemId) : undefined;
  const resolvedItems = panelData?.resolvedItems ?? {};
  return itemIds
    .map((id): ItemSummary | undefined => {
      const resolved = resolvedItems[String(id)];
      if (resolved) return resolved;
      const details = ctx.getItemDetails(id);
      if (!details) return undefined;
      return { id: details.id, name: details.name, itemTypeId: details.itemTypeId };
    })
    .filter((item): item is ItemSummary => item !== undefined);
}

export const useItemsBulkFetchQueryWithPublished = ({ itemIds }: { itemIds: number[] }) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemsQuery = useItemsBulkFetchQuery({ itemIds }, undefined, !isPublished);

  const publishedQuery = useQuery<ItemSummary[] | undefined>({
    queryKey: [...commonQueryKeys.itemsBulkFetch.byParams({ ids: itemIds }), 'published'],
    queryFn: skipToken,
    initialData: () =>
      publishedContext ? resolvePublishedItems(publishedContext, itemIds) : undefined,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const itemsSummary = useMemo<ItemSummary[] | undefined>(
    () => itemsQuery.data?.map((i) => ({ id: i.id, name: i.name, itemTypeId: i.itemTypeId })),
    [itemsQuery.data],
  );

  if (isPublished) return publishedQuery;
  return { ...itemsQuery, data: itemsSummary };
};
